import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";

const PrivacyPolicy = () => {
  const { language, t } = useLanguage();
  const location = useLocation();
  
  useEffect(() => {
    // Force scroll to top when component mounts or location changes
    window.scrollTo(0, 0);
    
    // Add a small delay to ensure it happens after any browser auto-scroll
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    
    // Add another timeout with longer delay as a fallback
    const secondTimer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 500);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(secondTimer);
    };
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900 transition-colors duration-300">
      <SEO
        title="Adatkezelési tájékoztató — BlueTech"
        description="A BlueTech Energy Solutions adatkezelési tájékoztatója: hogyan kezeljük és védjük az ön személyes adatait."
        path="/adatkezeles"
      />
      <Navbar />
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-8 dark:text-white">
              {t('privacy_policy')}
            </h1>
            
            {language === 'hu' ? (
              <HungarianPrivacyPolicy />
            ) : (
              <EnglishPrivacyPolicy />
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const HungarianPrivacyPolicy = () => (
  <div className="prose dark:prose-invert max-w-none [&>h2]:font-bold [&>p]:mb-10">
    <h2>1. Bevezetés</h2>
    <p>
      A Blue Tech Energy Solutions Kft. (továbbiakban: "szolgáltató" vagy "adatkezelő") elkötelezett a személyes adatok védelme iránt. 
      Jelen adatkezelési tájékoztató célja, hogy világosan és átláthatóan tájékoztassa Önt arról, hogy milyen személyes adatokat gyűjtünk, 
      azokat milyen célból használjuk fel, és milyen jogai vannak az adatkezeléssel kapcsolatban.
    </p>
    
    <h2>2. Az adatkezelő adatai</h2>
    <p>
      <strong>Cégnév:</strong> Blue Tech Energy Solutions Kft.<br />
      <strong>Székhely:</strong> 6044 Kecskemét, Bodócs Gyula utca 21.
    </p>
    
    <h2>3. A kezelt adatok köre és az adatkezelés célja</h2>
    <p>
      Weboldalunkon keresztül az alábbi személyes adatokat gyűjtjük Öntől:
    </p>
    
    <h3>Kapcsolatfelvételi űrlap esetén:</h3>
    <ul>
      <li><strong>Név:</strong> Az Ön beazonosításához, megszólításához szükséges.</li>
      <li><strong>E-mail cím:</strong> Ezen keresztül tudunk Önnek válaszolni.</li>
      <li><strong>Üzenet tartalma:</strong> Az Ön által küldött információ, kérdés vagy érdeklődés.</li>
    </ul>
    
    <h3>Feliratkozási űrlap esetén:</h3>
    <ul>
      <li><strong>Cég neve:</strong> A vállalkozás beazonosításához szükséges.</li>
      <li><strong>Kapcsolattartó neve:</strong> Az Ön beazonosításához, megszólításához szükséges.</li>
      <li><strong>E-mail cím:</strong> Ezen keresztül tudunk Önnek válaszolni, tájékoztatást küldeni.</li>
      <li><strong>Telefonszám:</strong> Opcionális, közvetlen kapcsolatfelvételhez használjuk.</li>
      <li><strong>Vállalkozás típusa:</strong> Segít nekünk az Ön igényeinek pontosabb megértésében.</li>
      <li><strong>Tervezési szolgáltatás iránti érdeklődés:</strong> Az Ön igényeinek megfelelő ajánlat kidolgozásához használjuk.</li>
    </ul>
    
    <p>
      A fent említett adatokat kizárólag az Önnel való kapcsolatfelvétel és a Blue Tech Energy Solutions Kft. megoldásaival kapcsolatos tájékoztatás 
      céljából használjuk fel. Az adatokat marketing célokra csak az Ön kifejezett hozzájárulása esetén használjuk.
    </p>
    
    <h2>4. Az adatkezelés jogalapja</h2>
    <p>
      Az adatkezelés jogalapja az Ön önkéntes, kifejezett és tájékozott hozzájárulása, amelyet az űrlap kitöltésével és elküldésével ad meg. 
      A hozzájárulását bármikor visszavonhatja az adatkezelőhöz intézett kérelemmel.
    </p>
    
    <h2>5. Az adatkezelés időtartama</h2>
    <p>
      Az Ön által megadott személyes adatokat a kapcsolatfelvételi célból történő adatkezelés esetében az Ön hozzájárulásának visszavonásáig, 
      de legfeljebb az utolsó kapcsolatfelvételtől számított 2 évig tároljuk. A feliratkozási űrlap esetében az adatokat az Ön hozzájárulásának 
      visszavonásáig, de legfeljebb 5 évig tároljuk.
    </p>
    
    <h2>6. Adatfeldolgozók</h2>
    <p>
      Az adatok feldolgozását részben külső szolgáltatók segítségével végezzük. Ezek a szolgáltatók adatfeldolgozóként járnak el, 
      és a személyes adatokat kizárólag utasításaink szerint kezelhetik.
    </p>
    
    <h2>7. Az Ön jogai</h2>
    <p>
      Az adatkezeléssel kapcsolatban Önnek az alábbi jogai vannak:
    </p>
    <ul>
      <li><strong>Hozzáférési jog:</strong> Jogosult tájékoztatást kapni az általunk kezelt személyes adatairól.</li>
      <li><strong>Helyesbítéshez való jog:</strong> Kérheti a pontatlan adatok helyesbítését.</li>
      <li><strong>Törléshez való jog:</strong> Bizonyos esetekben kérheti adatai törlését.</li>
      <li><strong>Az adatkezelés korlátozásához való jog:</strong> Bizonyos esetekben kérheti adatai kezelésének korlátozását.</li>
      <li><strong>Adathordozhatósághoz való jog:</strong> Kérheti, hogy az Önre vonatkozó adatokat géppel olvasható formátumban átadjuk.</li>
      <li><strong>Tiltakozáshoz való jog:</strong> Jogosult tiltakozni az adatkezelés ellen.</li>
    </ul>
    
    <div className="mb-10"></div>
    
    <h2>8. Adatbiztonság</h2>
    <p>
      Elkötelezettek vagyunk a személyes adatok biztonságának garantálása mellett. A megfelelő technikai és szervezési intézkedéseket 
      alkalmazunk az adatok biztonságának megőrzése érdekében a véletlen vagy jogellenes megsemmisítés, elvesztés, megváltoztatás, 
      jogosulatlan nyilvánosságra hozatal vagy hozzáférés ellen.
    </p>
    
    <h2>9. Jogérvényesítési lehetőségek</h2>
    <p>
      Ha úgy érzi, hogy adatainak kezelése kapcsán sérelem érte, kérjük, először vegye fel a kapcsolatot velünk a fenti elérhetőségeken. 
      Amennyiben nem sikerül megoldást találnunk, panaszt nyújthat be a Nemzeti Adatvédelmi és Információszabadság Hatósághoz:
    </p>
    <p>
      <strong>Nemzeti Adatvédelmi és Információszabadság Hatóság</strong><br />
      1055 Budapest, Falk Miksa utca 9-11.<br />
      Postacím: 1363 Budapest, Pf.: 9.<br />
      Telefon: +36 1 391 1400<br />
      E-mail: ugyfelszolgalat@naih.hu<br />
      Weboldal: naih.hu
    </p>
    <p>
      Jogainak megsértése esetén bírósághoz is fordulhat.
    </p>
    
    <h2>10. A tájékoztató módosítása</h2>
    <p>
      Fenntartjuk a jogot jelen adatkezelési tájékoztató módosítására. Az esetleges változásokról megfelelő módon tájékoztatjuk Önt.
    </p>
    
    <p>Utolsó frissítés: 2025.03.20</p>
  </div>
);

const EnglishPrivacyPolicy = () => (
  <div className="prose dark:prose-invert max-w-none [&>h2]:font-bold [&>p]:mb-10">
    <h2>1. Introduction</h2>
    <p>
      Blue Tech Energy Solutions Kft. ("we" or "data controller") is committed to protecting personal data. 
      This privacy policy explains what personal data we collect, how we use it, and what rights you have regarding data processing.
    </p>
    
    <h2>2. Data Controller Information</h2>
    <p>
      <strong>Company Name:</strong> Blue Tech Energy Solutions Kft.<br />
      <strong>Registered Address:</strong> 6044 Kecskemét, Bodócs Gyula utca 21., Hungary
    </p>
    
    <h2>3. Data We Collect and Purpose of Processing</h2>
    <p>
      When you contact us via our website, we may collect the following personal data:
    </p>
    
    <h3>Contact Form</h3>
    <ul>
      <li><strong>Name:</strong> Required for identification and addressing you.</li>
      <li><strong>Email Address:</strong> Used to respond to your inquiry.</li>
      <li><strong>Message Content:</strong> Your provided information, question, or request.</li>
    </ul>
    
    <h3>Subscription Form</h3>
    <ul>
      <li><strong>Company Name:</strong> Required for business identification.</li>
      <li><strong>Contact Person's Name:</strong> Needed for addressing you.</li>
      <li><strong>Email Address:</strong> Used for responses and updates.</li>
      <li><strong>Phone Number (optional):</strong> For direct communication.</li>
      <li><strong>Business Type:</strong> Helps us understand your needs.</li>
      <li><strong>Interest in Design Services:</strong> Helps us tailor our offers.</li>
    </ul>
    
    <p>
      Your data is used exclusively for communication and information about Blue Tech Energy Solutions Kft. solutions. 
      We will only use your data for marketing purposes if you provide explicit consent.
    </p>
    
    <h2>4. Legal Basis for Processing</h2>
    <p>
      We process your data based on your voluntary, explicit, and informed consent, given by submitting the form. 
      You may withdraw your consent at any time by contacting us.
    </p>
    
    <h2>5. Data Retention Period</h2>
    <p>
      <strong>Contact Form:</strong> Data is stored until you withdraw consent, but no longer than 2 years from the last contact.<br />
      <strong>Subscription Form:</strong> Data is stored until consent is withdrawn, but no longer than 5 years.
    </p>
    
    <h2>6. Data Processors</h2>
    <p>
      We may use third-party service providers for data processing, who act as data processors and process your data only according to our instructions.
    </p>
    
    <h2>7. Your Rights</h2>
    <p>
      You have the right to:
    </p>
    <ul>
      <li><strong>Access</strong> your personal data.</li>
      <li>Request <strong>correction</strong> of inaccurate data.</li>
      <li>Request data <strong>deletion</strong> under certain conditions.</li>
      <li><strong>Restrict</strong> data processing in certain cases.</li>
      <li>Request <strong>data portability</strong> in a machine-readable format.</li>
      <li><strong>Object</strong> to data processing.</li>
    </ul>
    
    <div className="mb-10"></div>
    
    <h2>8. Data Security</h2>
    <p>
      We implement technical and organizational measures to ensure data security, 
      preventing unauthorized access, alteration, disclosure, or loss.
    </p>
    
    <h2>9. Legal Remedies</h2>
    <p>
      If you believe your data has been mishandled, please contact us first. If we cannot resolve the issue, 
      you may file a complaint with the Hungarian National Authority for Data Protection and Freedom of Information (NAIH):
    </p>
    <p>
      <strong>National Authority for Data Protection and Freedom of Information</strong><br />
      1055 Budapest, Falk Miksa utca 9-11., Hungary<br />
      Mailing Address: 1363 Budapest, Pf.: 9.<br />
      Phone: +36 1 391 1400<br />
      Email: ugyfelszolgalat@naih.hu<br />
      Website: naih.hu
    </p>
    <p>
      You may also take legal action if your rights are violated.
    </p>
    
    <h2>10. Changes to This Privacy Policy</h2>
    <p>
      We reserve the right to update this privacy policy. We will inform you of any changes appropriately.
    </p>
    
    <p>Last Updated: March 20, 2025</p>
  </div>
);

export default PrivacyPolicy;
